import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { encryptData, decryptData } from "../../utilities/cryptoUtils";

import { jwtDecode } from "jwt-decode";

interface User {
  email: string;
  phone: string;
  role: string;
  tradingModes: ("crypto" | "stocks")[];
  userId: string;
}

interface JwtPayload {
  userId: string;
  exp: number;
}

interface AuthContextType {
  token: string | null;
  user: User | null;
  loading: boolean;
  currentTradingMode: "stocks"|"crypto";
  authorized: boolean;
  isStocksEnabled: boolean;
  isCryptoEnabled: boolean;
  setToken: (token: string | null) => void;
  setCurrentTradingMode: (mode: "stocks" | "crypto") => void;
  setUser: (user: User | null) => void;
  logout: () => void;
  login: (token: string, user: User) => void;
  // verifyAuth: () => Promise<boolean>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const AuthProvider = ({ children }: { children: React.ReactNode }) => {
  const [token, setTokenState] = useState<string | null>(null);
  const [user, setUserState] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [authorized, setAuthorized] = useState(false);
  const [currentTradingMode, setCurrentTradingModeState] = useState<"stocks" | "crypto">("stocks");

  // Derived states
  const isStocksEnabled = useMemo(() => 
    user?.tradingModes?.includes("stocks") ?? false, 
    [user]
  );
  
  const isCryptoEnabled = useMemo(() => 
    user?.tradingModes?.includes("crypto") ?? false, 
    [user]
  );

  const setToken = useCallback((value: string | null) => {
    if (value) {
      localStorage.setItem("token", value);
      setTokenState(value);
    } else {
        console.log("sdfsdf callsfds",value)
      localStorage.removeItem("token");
      setTokenState(null);
    }
  }, []);

  const setUser = useCallback((value: User | null) => {
    if (value) {
      const enData = encryptData(value);
      if (enData) {
        localStorage.setItem("user", enData);
        setUserState(value);
      }
    } else {
      localStorage.removeItem("user");
      setUserState(null);
    }
  }, []);


const setCurrentTradingMode = useCallback((mode: "stocks" | "crypto") => {
  setCurrentTradingModeState(mode);
}, []);


  const logout = useCallback(() => {
    setToken(null);
    setUser(null);
    setAuthorized(false);
  }, [setToken, setUser]);

  const login = useCallback((token: string, user: User) => {
    if (!token || !user) return;
    try {
      const decoded = jwtDecode<JwtPayload>(token);
      if (decoded.userId !== user.userId) throw new Error("User ID mismatch");
      if (Date.now() > decoded.exp * 1000) throw new Error("Token expired");

      setToken(token);
      setUser(user);
      setAuthorized(true);
      setCurrentTradingModeState(
  Array.isArray(user.tradingModes) && user.tradingModes.length > 0
    ? user.tradingModes[0] :"stocks"

);

    } catch (error) {
      console.error("Login failed:", error);
      logout();
    }
  }, [setToken, setUser, logout]);

  const verifyAuth = useCallback(async () => {
    setLoading(true);
    try {
      // const response = await GetAuthMe();
      // if (!response.ok) throw new Error("Auth failed");

      // const result = await response.json();
      // if (result.status === "success") {
      //   const userData = {
      //     email: result.data.email,
      //     phone: result.data.phone,
      //     role: result.data.role,
      //     userId: result.data.userId,
      //     tradingModes: result.data.tradingModes || [],
      //   };
      //   login(result.data.token, userData);
      //   return true;
      // }
      // throw new Error("Invalid response");
    } catch (error) {
      logout();
      return false;
    } finally {
      setLoading(false);
    }
  }, [login, logout]);

  useEffect(() => {
    let isMounted = true;
    const validateToken = async () => {
      const storedToken = localStorage.getItem("token");
      const storedUser = localStorage.getItem("user");

      if (storedToken && storedUser) {
        try {
          const decoded = jwtDecode<JwtPayload>(storedToken);
          const decrypted = decryptData(storedUser) as User;

          if (Date.now() > decoded.exp * 1000) throw new Error("Token expired");
          if (decrypted?.userId !== decoded.userId) throw new Error("User mismatch");

          if (isMounted) {
            setTokenState(storedToken);
            setUserState(decrypted);
            setAuthorized(true);
            setCurrentTradingModeState(
  Array.isArray(decrypted.tradingModes) && decrypted.tradingModes.length > 0
    ? decrypted.tradingModes[0]
    : "stocks"
);
          }
        } catch (error) {
          console.error("Auth validation failed:", error);
          await verifyAuth();
        }
      } else if (isMounted) {
        logout();
      }
      if (isMounted) setLoading(false);
    };

    validateToken();
    return () => { isMounted = false; };
  }, [verifyAuth, logout]);

  return (
    <AuthContext.Provider
      value={{
        token,
        user,
        loading,
        authorized,
        currentTradingMode,
        isStocksEnabled,
        isCryptoEnabled,
        setToken,
        setUser,
        setCurrentTradingMode,
        logout,
        login,
        // verifyAuth,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) throw new Error("useAuth must be used within AuthProvider");
  return context;
};  